import { FC } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { css, Interpolation, Theme } from '@emotion/react';
import { ResizableBox } from 'react-resizable';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { CHATBOT_DIMENSIONS } from '../../constant';
import { useChatbotAssistant } from '../../provider/chatbot-assistant.provider';
import { BouncingLoader } from '../bouncing-loader';
import { ChatBody } from '../chat-body';
import { ChatInput } from '../chat-input';
import { AssistantHeader } from '../header';

interface ChatbotWidgetProps {
  isDraggable?: boolean;
  dragHandleProps?: Pick<ReturnType<typeof useDraggable>, 'attributes' | 'listeners'>;
  containerStyles?: Interpolation<Theme>;
  hideHeaderActions?: boolean;
}

export const ResizableChatbotWidget: FC<ChatbotWidgetProps> = (props) => {
  return (
    <ResizableBox
      width={CHATBOT_DIMENSIONS.DEFAULT.width}
      height={CHATBOT_DIMENSIONS.DEFAULT.height}
      minConstraints={[CHATBOT_DIMENSIONS.MIN.width, CHATBOT_DIMENSIONS.MIN.height]}
      maxConstraints={[CHATBOT_DIMENSIONS.MAX.width, CHATBOT_DIMENSIONS.MAX.height]}
      css={{ position: 'relative' }}
      handle={
        <div css={styles.resizeHandle}>
          <Icon name='resize-handle-small' size={12} />
        </div>
      }
    >
      <ChatbotWidget {...props} />
    </ResizableBox>
  );
};

export const ChatbotWidget: FC<ChatbotWidgetProps> = ({
  isDraggable,
  dragHandleProps,
  containerStyles,
  hideHeaderActions,
}) => {
  const { t } = useTranslation('aiOnboarding');
  const { isSending, messages, sendMessage, toggleChatbotOpen, openPopoutWindow } = useChatbotAssistant();

  return (
    <div css={[styles.container, containerStyles]}>
      <AssistantHeader>
        <AssistantHeader.GradientBar>
          {isDraggable && (
            <>
              <Icon name='drag-handle' color='white' size={12} />
              <div css={styles.dragHandleArea} {...dragHandleProps?.attributes} {...dragHandleProps?.listeners} />
            </>
          )}
        </AssistantHeader.GradientBar>
        <AssistantHeader.HeaderBar>
          <AssistantHeader.Title>{t('Weave AI')}</AssistantHeader.Title>
          {!hideHeaderActions && (
            <AssistantHeader.PanelControls onMinimizeAction={toggleChatbotOpen} onPopoutAction={openPopoutWindow} />
          )}
        </AssistantHeader.HeaderBar>
      </AssistantHeader>
      <ChatBody>
        {messages.length > 0 ? <ChatBody.ChatMessageList messages={messages} /> : <ChatBody.ChatWelcomeMessage />}
      </ChatBody>
      {isSending && <BouncingLoader containerStyles={{ marginLeft: theme.spacing(2) }} />}
      <ChatInput>
        <ChatInput.TextBox onSend={sendMessage} />
      </ChatInput>
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    position: relative;
    border-radius: ${theme.borderRadius.large};
    border: 1px solid ${theme.colors.neutral20};
    overflow-y: auto;
    background-color: ${theme.colors.white};
    height: 100%;
  `,
  hidden: css`
    display: none;
  `,
  dragHandleArea: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100% + 8px);
    cursor: grab;
  `,
  resizeHandle: css`
    position: absolute;
    bottom: ${theme.spacing(0.5)};
    right: ${theme.spacing(0.5)};
    cursor: nwse-resize;
    user-select: none;
    svg {
      color: ${theme.colors.neutral30};
    }
  `,
};
